import { useState, useCallback } from "react";
import Condition from "../models/conditions/Condition";
import { ConditionsToEdit } from "../components/conditions/ConditionsEditor";
import { useStoreDispatch, useStoreSelector } from "../store/Hooks";
import { navSliceActions } from "../store/slices/NavSlice";
import { DatabaseTable } from "../models/BaseTable";
import {
  postgrest_operators,
  runDatabaseFunction,
} from "../helpers/HttpHelper";
import InteractionNode from "../models/interactions/InteractionNode";
import Interaction from "../models/interactions/Interaction";
import InteractionNodeInterval from "../models/interactions/InteractionNodeInterval";
import Interval from "../models/communications/Interval";

// Returns title for Conditions Builder based on table
// Requires multiple fetches, so should only be used if necessary
const getTitle = async (table: string, tableID: any): Promise<string> => {
  const idString = String(tableID);
  if (table === "Condition") {
    try {
      const condition = await Condition.fetchRow(idString);
      return "Condition Set " + condition.name ?? idString;
    } catch (error) {
      return "Condition Set " + idString;
    }
  } else if (table === "InteractionNode") {
    try {
      const interactionNode = await InteractionNode.fetchRow(idString);
      if (typeof interactionNode === "undefined") {
        throw new Error("Can't find Interaction Node");
      }

      const interaction = await Interaction.fetchRow(interactionNode.child_interaction_id);
      if (typeof interaction === "undefined") {
        throw new Error("Can't find Interaction");
      }
      return "Interaction " + interaction.title ?? idString;
    } catch (error) {
      return "Interaction Node " + idString;
    }
  } else if (table === "InteractionNodeInterval") {
    try {
      const interactionNodeInterval = await InteractionNodeInterval.fetchRow(
        idString,
      );
      const interactionTitle = getTitle(
        "InteractionNode",
        interactionNodeInterval.interaction_node_id,
      );

      const interval = await Interval.fetchRow(
        interactionNodeInterval.interval_id,
      );

      return interactionTitle + " For Interval " + interval.name ?? idString;
    } catch (error) {
      return "Interaction Node Interval " + idString;
    }
  } else {
    return table + " ID " + idString;
  }
};

const fetchConditions = async (
  table: string,
  tableID: any,
  setID: number,
): Promise<ConditionsToEdit> => {
  const idString = String(tableID);
  let parentConditions: Condition[] = [];
  let directConditions: Condition[] = [];
  if (table === "Condition") {
    directConditions = (await Condition.queryTable([
      {
        operator: postgrest_operators.eq,
        column: "set_condition_id ",
        value: idString,
      },
    ])) as Condition[];
  } else {
    let response;
    if (table === "InteractionNode") {
      response = await runDatabaseFunction("get_interaction_node_conditions", {
        interaction_node_id: parseInt(idString),
        set_interaction_id: setID,
      });
    } else if (table === "InteractionNodeInterval") {
      response = await runDatabaseFunction(
        "get_interaction_node_interval_conditions",
        {
          interaction_node_interval_id: parseInt(idString),
          set_interaction_id: setID,
        },
      );
    }
    if (typeof response === "undefined") {
      throw new Error("Invalid table sent");
    }

    if (!response.ok) {
      throw new Error("Request failed!");
    }

    const responseJSON = await response.json();

    if (typeof responseJSON !== "undefined") {
      if (Array.isArray(responseJSON.direct_conditions)) {
        directConditions = responseJSON.direct_conditions.map((json: any) =>
          Condition.fromJSON(json),
        );
      }
      if (Array.isArray(responseJSON.parent_conditions)) {
        parentConditions = responseJSON.parent_conditions.map((json: any) =>
          Condition.fromJSON(json),
        );
      }
    }
  }

  return {
    parentConditions: parentConditions,
    directConditions: directConditions,
  };
};

const useConditions = () => {
  const dispatch = useStoreDispatch();
  const setID = useStoreSelector(
    (state) => state.interactionTree.RootInteractionID,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const openConditionsBuilder = async (
    resource: typeof DatabaseTable,
    resourceID: any,
    conditionID: number | null,
    loadInDrawer = false,
    editorTitle = "",
  ) => {
    const resourceName = resource.tableName;

    try {
      if (editorTitle.trim() === "") {
        editorTitle = await getTitle(resourceName, resourceID);
      }
      const conditions = await fetchConditions(resourceName, resourceID, setID);
      if (loadInDrawer) {
        dispatch(
          navSliceActions.openRightDrawer({
            content: {
              conditions: conditions,
              title: editorTitle,
            },
            resource,
            resourceName,
            resourceID,
            type: "conditions",
            extraData: {
              setID: conditionID,
            },
          }),
        );
      } else {
        // Load Conditions Builder component directly
      }
    } catch (err: any) {
      setError(err.message || "Unable to open Conditions Builder");
    }
    setIsLoading(false);
  };

  const getConditions = async (table: typeof DatabaseTable, tableID: any) => {
    return await fetchConditions(table.tableName, tableID, setID);
  };

  return {
    isLoading,
    error,
    openConditionsBuilder,
    getConditions,
  };
};

export default useConditions;
